import { Button } from '@/components/ui/button';
import { useChatStore } from '@/stores/chatStore';
import { getSuggestedPrompts } from '@/services/ai/suggestedPrompts';
import type { SuggestedPrompt } from '@/services/ai/types';
import { Loader2, Sparkles } from 'lucide-react';

interface SuggestedPromptsProps {
  readonly onLaunchRuntimeAction?: (prompt: SuggestedPrompt) => void;
  readonly isRuntimePlanning?: boolean;
}

export default function SuggestedPrompts({
  onLaunchRuntimeAction,
  isRuntimePlanning = false,
}: SuggestedPromptsProps = {}) {
  const currentRoute = useChatStore(s => s.currentRoute);
  const sendMessage = useChatStore(s => s.sendMessage);
  const isStreaming = useChatStore(s => s.isStreaming);
  const prompts = getSuggestedPrompts(currentRoute);

  if (prompts.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-4 py-2">
      {prompts.map((prompt) => (
        <Button
          key={prompt.actionId ?? prompt.text}
          variant="outline"
          size="sm"
          disabled={isStreaming || (isRuntimePlanning && prompt.launchMode === 'runtime')}
          onClick={() => {
            if (prompt.launchMode === 'runtime' && onLaunchRuntimeAction) {
              onLaunchRuntimeAction(prompt);
              return;
            }
            sendMessage(prompt.text);
          }}
          className="h-auto rounded-full px-2.5 py-1 text-xs text-slate-600 transition duration-200 hover:border-brand-red/40 hover:text-brand-red"
          title={prompt.description}
          data-testid={prompt.actionId ? `button-ai-action-${prompt.actionId}` : undefined}
        >
          {isRuntimePlanning && prompt.launchMode === 'runtime' ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin text-brand-red" />
          ) : (
            <Sparkles className="mr-1 h-3 w-3 text-brand-red" />
          )}
          {prompt.capability ? (
            <span className="mr-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              {prompt.capability}
            </span>
          ) : null}
          {prompt.text}
        </Button>
      ))}
    </div>
  );
}
